import { useState, useEffect } from 'react'
import { Building2, MapPin, DollarSign, TrendingUp, AlertCircle, CheckCircle, Search } from 'lucide-react'

interface Client {
  id: string
  name: string
  type: string
  industry: string
  country: string
  status: string
  total_aum: number
  risk_rating: string
  onboarding_date: string
  relationship_manager_name: string
  deal_count: number
  active_deal_value: number
}

interface Summary {
  total_clients: number
  active_clients: number
  total_aum: number
  total_active_deal_value: number
}

export default function ClientsPage() {
  const [clients, setClients] = useState<Client[]>([])
  const [summary, setSummary] = useState<Summary | null>(null)
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [industryFilter, setIndustryFilter] = useState<string>('')

  useEffect(() => {
    fetchClients()
  }, [statusFilter, industryFilter])

  const fetchClients = async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      if (statusFilter) params.append('status', statusFilter)
      if (industryFilter) params.append('industry', industryFilter)

      const response = await fetch(`/api/clients?${params.toString()}`)
      const data = await response.json()
      
      if (data.success) {
        setClients(data.data)
        setSummary(data.summary)
      }
    } catch (error) {
      console.error('Error fetching clients:', error)
    } finally {
      setLoading(false)
    }
  }

  const filteredClients = clients.filter(client =>
    client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.industry.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.country.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const getRiskColor = (rating: string) => {
    switch (rating.toLowerCase()) {
      case 'low': return 'text-green-600 bg-green-100'
      case 'medium': return 'text-yellow-600 bg-yellow-100'
      case 'high': return 'text-red-600 bg-red-100'
      default: return 'text-gray-600 bg-gray-100'
    }
  }

  const getStatusIcon = (status: string) => {
    return status === 'active' ? (
      <CheckCircle className="w-4 h-4 text-green-600" />
    ) : (
      <AlertCircle className="w-4 h-4 text-gray-400" />
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-royal-blue"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-dashboard-title text-royal-blue-700 mb-2">Client Portfolio</h1>
        <p className="text-graphite-grey-500">
          Manage client relationships and track engagement across the portfolio
        </p>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 bg-royal-blue-100 rounded-lg flex items-center justify-center">
                <Building2 className="w-6 h-6 text-royal-blue-600" />
              </div>
            </div>
            <p className="text-sm text-graphite-grey mb-1">Total Clients</p>
            <p className="text-3xl font-bold text-royal-blue">{summary.total_clients}</p>
            <p className="text-xs text-green-600 mt-1">{summary.active_clients} active</p>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 bg-golden-100 rounded-lg flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-golden-600" />
              </div>
            </div>
            <p className="text-sm text-graphite-grey mb-1">Total AUM</p>
            <p className="text-3xl font-bold text-golden">
              ${(summary.total_aum / 1000000).toFixed(1)}M
            </p>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
            </div>
            <p className="text-sm text-graphite-grey mb-1">Active Deals Value</p>
            <p className="text-3xl font-bold text-green-600">
              ${(summary.total_active_deal_value / 1000).toFixed(0)}K
            </p>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <MapPin className="w-6 h-6 text-purple-600" />
              </div>
            </div>
            <p className="text-sm text-graphite-grey mb-1">Countries</p>
            <p className="text-3xl font-bold text-purple-600">
              {new Set(clients.map(c => c.country)).size}
            </p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search clients..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-royal-blue focus:border-transparent"
            />
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-royal-blue focus:border-transparent"
          >
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="prospect">Prospect</option>
            <option value="inactive">Inactive</option>
          </select>

          {/* Industry Filter */}
          <select
            value={industryFilter}
            onChange={(e) => setIndustryFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-royal-blue focus:border-transparent"
          >
            <option value="">All Industries</option>
            {Array.from(new Set(clients.map(c => c.industry))).map(industry => (
              <option key={industry} value={industry}>{industry}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Client Cards Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {filteredClients.map(client => (
          <div
            key={client.id}
            className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow cursor-pointer"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  {getStatusIcon(client.status)}
                  <h3 className="text-lg font-semibold text-vampire-black">{client.name}</h3>
                </div>
                <p className="text-sm text-graphite-grey">{client.industry}</p>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${getRiskColor(client.risk_rating)}`}>
                {client.risk_rating} risk
              </span>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <p className="text-xs text-graphite-grey mb-1">AUM</p>
                <p className="font-semibold text-royal-blue">
                  ${(client.total_aum / 1000000).toFixed(1)}M
                </p>
              </div>
              <div>
                <p className="text-xs text-graphite-grey mb-1">Active Deals</p>
                <p className="font-semibold text-green-600">
                  {client.deal_count > 0 ? `${client.deal_count} (${(client.active_deal_value / 1000).toFixed(0)}K)` : 'None'}
                </p>
              </div>
            </div>

            <div className="flex items-center justify-between pt-4 border-t border-gray-100">
              <div className="flex items-center gap-2 text-sm text-graphite-grey">
                <MapPin className="w-4 h-4" />
                {client.country}
              </div>
              <div className="text-xs text-graphite-grey">
                RM: {client.relationship_manager_name}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {filteredClients.length === 0 && (
        <div className="bg-white p-12 rounded-lg border border-gray-200 text-center">
          <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-graphite-grey">No clients found matching your filters</p>
        </div>
      )}
    </div>
  )
}
